import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from '../../../components/AppIcon';

const PerformanceMetrics = () => {
  const [metrics, setMetrics] = useState({});
  const [timeRange, setTimeRange] = useState('today');
  const [hourlyData, setHourlyData] = useState([]);

  useEffect(() => {
    // Mock performance metrics
    const mockMetrics = {
      totalCalls: 1247,
      activeCalls: 23,
      waitingCalls: 8,
      avgWaitTime: "2:34",
      serviceLevel: 87.5,
      abandonment: 4.2,
      avgHandleTime: "4:12",
      satisfaction: 4.3
    };

    const mockHourlyData = [
      { hour: '09:00', calls: 45, serviceLevel: 92 },
      { hour: '10:00', calls: 67, serviceLevel: 89 },
      { hour: '11:00', calls: 89, serviceLevel: 85 },
      { hour: '12:00', calls: 78, serviceLevel: 88 },
      { hour: '13:00', calls: 56, serviceLevel: 91 },
      { hour: '14:00', calls: 92, serviceLevel: 83 },
      { hour: '15:00', calls: 108, serviceLevel: 79 },
      { hour: '16:00', calls: 134, serviceLevel: 76 },
      { hour: '17:00', calls: 98, serviceLevel: 82 }
    ];

    setMetrics(mockMetrics);
    setHourlyData(mockHourlyData);
  }, [timeRange]);

  const getMetricColor = (value, type) => {
    switch (type) {
      case 'serviceLevel':
        return value >= 80 ? 'text-green-600' : value >= 70 ? 'text-yellow-600' : 'text-red-600';
      case 'abandonment':
        return value <= 5 ? 'text-green-600' : value <= 10 ? 'text-yellow-600' : 'text-red-600';
      case 'satisfaction':
        return value >= 4.0 ? 'text-green-600' : value >= 3.5 ? 'text-yellow-600' : 'text-red-600';
      default:
        return 'text-slate-900';
    }
  };

  const getMetricIcon = (type) => {
    switch (type) {
      case 'calls': return 'Phone';
      case 'time': return 'Clock';
      case 'level': return 'TrendingUp';
      case 'satisfaction': return 'Star';
      default: return 'BarChart3';
    }
  };

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900">Performans Metrikleri</h2>
        <div className="flex items-center space-x-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e?.target?.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="today">Bugün</option>
            <option value="week">Bu Hafta</option>
            <option value="month">Bu Ay</option>
          </select>
          <button className="p-2 text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg hover:bg-slate-50">
            <Icon name="RefreshCw" size={16} />
          </button>
        </div>
      </div>
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">Toplam Arama</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{metrics?.totalCalls?.toLocaleString('tr-TR')}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Icon name="Phone" size={24} color="#2563eb" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <Icon name="TrendingUp" size={16} className="text-green-600 mr-1" />
            <span className="text-sm text-green-600">+12% önceki güne göre</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">Aktif Aramalar</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{metrics?.activeCalls}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Icon name="PhoneCall" size={24} color="#059669" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
            <span className="text-sm text-slate-600">Gerçek zamanlı</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">Bekleyen Aramalar</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{metrics?.waitingCalls}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Icon name="Clock" size={24} color="#d97706" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <span className="text-sm text-slate-600">Ort. bekleme: {metrics?.avgWaitTime}</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">Hizmet Seviyesi</p>
              <p className={`text-2xl font-bold mt-1 ${getMetricColor(metrics?.serviceLevel, 'serviceLevel')}`}>
                {metrics?.serviceLevel}%
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Icon name="TrendingUp" size={24} color="#7c3aed" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <span className="text-sm text-slate-600">Hedef: 80%</span>
          </div>
        </div>
      </div>
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hourly Call Volume */}
        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Saatlik Arama Hacmi</h3>
            <Icon name="BarChart3" size={20} className="text-slate-400" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis 
                  dataKey="hour" 
                  stroke="#64748b"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#64748b"
                  fontSize={12}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="calls" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Service Level Trend */}
        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Hizmet Seviyesi Trendi</h3>
            <Icon name="TrendingUp" size={20} className="text-slate-400" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis 
                  dataKey="hour" 
                  stroke="#64748b"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#64748b"
                  fontSize={12}
                  domain={[0, 100]}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="serviceLevel" 
                  stroke="#10b981" 
                  strokeWidth={3}
                  dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-slate-600">Terk Oranı</h4>
            <Icon name="UserX" size={16} className="text-slate-400" />
          </div>
          <p className={`text-xl font-bold ${getMetricColor(metrics?.abandonment, 'abandonment')}`}>
            {metrics?.abandonment}%
          </p>
          <p className="text-xs text-slate-500 mt-1">Hedef: &lt;5%</p>
        </div>

        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-slate-600">Ort. İşlem Süresi</h4>
            <Icon name="Clock" size={16} className="text-slate-400" />
          </div>
          <p className="text-xl font-bold text-slate-900">{metrics?.avgHandleTime}</p>
          <p className="text-xs text-slate-500 mt-1">Hedef: &lt;5:00</p>
        </div>

        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-slate-600">Müşteri Memnuniyeti</h4>
            <Icon name="Star" size={16} className="text-slate-400" />
          </div>
          <p className={`text-xl font-bold ${getMetricColor(metrics?.satisfaction, 'satisfaction')}`}>
            {metrics?.satisfaction}/5.0
          </p>
          <p className="text-xs text-slate-500 mt-1">Son 100 arama</p>
        </div>
      </div>
    </div>
  );
};

export default PerformanceMetrics;